<template>
  <div class="min-h-screen bg-background">
    <!-- Loading State -->
    <div v-if="loading" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      <p class="mt-4 text-white">Chargement du panier...</p>
    </div>

    <div v-else-if="cartItems.length === 0" class="container mx-auto px-4 py-16">
      <div class="text-center">
        <ShoppingBag class="h-24 w-24 text-muted-foreground mx-auto mb-6" />
        <h1 class="text-3xl font-bold text-foreground mb-4">
          Votre panier est vide
        </h1>
        <p class="text-lg text-muted-foreground mb-8">
          Découvrez notre collection et ajoutez vos produits préférés
        </p>
        <router-link to="/products">
          <button
            class="bg-primary hover:bg-primary-hover text-primary-foreground h-12 px-8 rounded-lg font-medium transition-normal">
            Continuer mes achats
          </button>
        </router-link>
      </div>
    </div>

    <div v-else class="container mx-auto px-4 py-8">
      <h1 class="text-2xl md:text-3xl font-bold text-foreground mb-8">
        Mon Panier ({{ totalItems }} article{{ totalItems > 1 ? 's' : '' }})
      </h1>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Cart Items -->
        <div class="lg:col-span-2 space-y-4">
          <div v-for="item in cartItems" :key="item.id" class="bg-card rounded-lg border border-border">
            <div class="p-4 md:p-6">
              <div class="flex flex-col sm:flex-row gap-4">
                <!-- Product Image -->
                <div class="flex-shrink-0">
                  <img :src="getProductImage(item.product)" :alt="item.product.name"
                    class="w-full sm:w-28 h-32 object-center object-cover rounded-lg bg-muted"
                    @error="handleImageError" />
                </div>

                <!-- Product Details -->
                <div class="flex-1 min-w-0">
                  <div class="flex flex-col sm:flex-row sm:justify-between gap-2">
                    <div>
                      <p class="text-xs text-muted-foreground mb-1">
                        {{ item.product.model?.category?.name || 'Non catégorisé' }}
                      </p>
                      <h3 class="font-semibold text-foreground truncate">
                        {{ item.product.name }}
                      </h3>
                      <p v-if="item.product.model?.brand?.name" class="text-xs text-muted-foreground">
                        Marque: {{ item.product.model.brand.name }}
                      </p>
                    </div>
                    <div class="text-right">
                      <p class="font-bold text-primary">
                        {{ formatPrice(getItemTotal(item)) }}
                      </p>
                      <p v-if="item.product.promo_price" class="text-sm text-muted-foreground line-through">
                        {{ formatPrice(item.product.price * item.quantity) }}
                      </p>
                    </div>
                  </div>

                  <!-- Configuration Options -->
                  <div class="mt-4 flex flex-wrap gap-3">
                    <!-- Size Selector -->
                    <div v-if="item.product.has_size && hasSizes(item.product)" class="flex flex-col gap-1">
                      <span class="text-xs text-muted-foreground">Taille</span>
                      <select 
                        :value="item.size" 
                        @change="updateSize(item.id, $event.target.value)"
                        class="w-24 h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal">
                        <option value="" disabled>Taille</option>
                        <option v-for="sizeOption in getSizesArray(item.product.sizes)" 
                                :key="sizeOption" 
                                :value="sizeOption"
                                :selected="item.size === sizeOption">
                          {{ sizeOption }}
                        </option>
                      </select>
                    </div>

                    <!-- Color Selector -->
                    <div v-if="item.product.has_color && hasColors(item.product)" class="flex flex-col gap-1">
                      <span class="text-xs text-muted-foreground">Couleur</span>
                      <select 
                        :value="item.color" 
                        @change="updateColor(item.id, $event.target.value)"
                        :style="`background: ${item.color}; color: ${getContrastColor(item.color)}`"
                        class="w-32 h-9 px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal">
                        <option value="" disabled>Couleur</option>
                        <option v-for="colorOption in getColorsArray(item.product.colors)" 
                                :key="colorOption" 
                                :value="colorOption"
                                :selected="item.color === colorOption"
                              
                                :style="`background: ${colorOption}; color: ${getContrastColor(colorOption)}`">
                          {{ colorOption }}
                        </option>
                      </select>
                    </div>

                    <!-- Quantity Selector -->
                    <div class="flex flex-col gap-1">
                      <span class="text-xs text-muted-foreground">Quantité</span>
                      <div class="flex items-center gap-2 border border-input rounded-lg px-2 h-9">
                        <button @click="decrementItem(item.id)" 
                                class="p-1 hover:bg-muted rounded transition-colors"
                                :disabled="item.quantity <= 1">
                          <Minus class="h-4 w-4" />
                        </button>
                        <span class="w-8 text-center font-medium">
                          {{ item.quantity }}
                        </span>
                        <button @click="incrementItem(item.id)" 
                                class="p-1 hover:bg-muted rounded transition-colors"
                                :disabled="item.quantity >= item.product.stock_quantity">
                          <Plus class="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Unit Price & Remove -->
                  <div class="mt-3 flex items-center justify-between">
                    <p class="text-sm text-muted-foreground">
                      Prix unitaire: {{ formatPrice(getItemPrice(item.product)) }}
                      <span v-if="item.product.promo_price" class="text-green-600 ml-1">
                        (-{{ calculateDiscount(item.product) }}%)
                      </span>
                    </p>
                    <button @click="removeFromCart(item.id)"
                      class="text-destructive hover:text-destructive/80 transition-colors flex items-center gap-1 text-sm">
                      <Trash2 class="h-4 w-4" />
                      <span class="hidden sm:inline">Supprimer</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Continue Shopping -->
          <div class="pt-4">
            <router-link to="/category" class="inline-flex items-center gap-2 text-primary hover:underline">
              <ArrowRight class="h-4 w-4 rotate-180" />
              Continuer mes achats
            </router-link>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="lg:col-span-1">
          <div class="sticky top-24 bg-card rounded-lg border border-border">
            <div class="p-6">
              <h2 class="text-xl font-bold text-foreground mb-6">
                Récapitulatif
              </h2>

              <!-- Promo Code -->
              <div class="mb-6">
                <div v-if="!appliedPromo" class="flex gap-2">
                  <input type="text" placeholder="Code promo" v-model="promoCode"
                    class="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal" />
                  <button @click="applyPromoCode"
                    class="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-3 rounded-md flex items-center justify-center transition-normal">
                    <Tag class="h-4 w-4" />
                  </button>
                </div>
                <div v-else class="flex items-center justify-between bg-primary/10 p-3 rounded-lg">
                  <div class="flex items-center gap-2">
                    <Tag class="h-4 w-4 text-primary" />
                    <span class="font-medium text-primary">{{ appliedPromo.code }}</span>
                    <span class="text-sm text-muted-foreground">
                      (-{{ appliedPromo.discount }}%)
                    </span>
                  </div>
                  <button @click="removePromoCode" class="text-muted-foreground hover:text-destructive">
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
              </div>

              <!-- Price Details -->
              <div class="space-y-3 mb-6">
                <div class="flex justify-between text-muted-foreground">
                  <span>Sous-total</span>
                  <span>{{ formatPrice(subtotal) }}</span>
                </div>
                <div v-if="savings > 0" class="flex justify-between text-green-600">
                  <span>Économies</span>
                  <span>-{{ formatPrice(savings) }}</span>
                </div>
                <div v-if="promoDiscount > 0" class="flex justify-between text-green-600">
                  <span>Réduction promo</span>
                  <span>-{{ formatPrice(promoDiscount) }}</span>
                </div>
                <div class="flex justify-between text-muted-foreground">
                  <span>Livraison</span>
                  <span>
                    <span v-if="shipping === 0" class="text-green-600">Gratuite</span>
                    <span v-else>{{ formatPrice(shipping) }}</span>
                  </span>
                </div>
                <p v-if="shipping > 0" class="text-xs text-muted-foreground">
                  Livraison gratuite à partir de 50 000 FCFA
                </p>
              </div>

              <div class="border-t pt-4 mb-6">
                <div class="flex justify-between text-lg font-bold text-foreground">
                  <span>Total</span>
                  <span class="text-primary">{{ formatPrice(total) }}</span>
                </div>
                <p class="text-xs text-muted-foreground mt-2">
                  TVA incluse
                </p>
              </div>

              <!-- Proceed to Checkout -->
              <div class="relative">
                <button @click="goToCheckout" 
                  :disabled="!canProceedToCheckout"
                  :class="[
                    'w-full bg-primary hover:bg-primary-hover text-primary-foreground h-12 px-4 rounded-lg font-medium flex items-center justify-center transition-normal disabled:opacity-50 disabled:cursor-not-allowed relative',
                    !canProceedToCheckout ? 'cursor-not-allowed' : ''
                  ]">
                  Passer la commande 
                  <ArrowRight class="ml-2 h-5 w-5" />
                </button>
                
                <!-- Overlay for disabled state -->
                <div v-if="!canProceedToCheckout" 
                  class="absolute cursor-not-allowed top-0 w-full h-full rounded-lg bg-gray-800 bg-opacity-30 z-10"></div>
              </div>

              <!-- Security Info -->
              <div class="mt-6 text-center">
                <p class="text-xs text-muted-foreground">
                  <Shield class="h-4 w-4 inline mr-1" />
                  Paiement 100% sécurisé
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  ArrowRight,
  Tag,
  Shield
} from 'lucide-vue-next';

export default {
  name: 'CartPage',
  components: {
    Minus,
    Plus,
    Trash2,
    ShoppingBag,
    ArrowRight,
    Tag,
    Shield
  },
  data() {
    return {
      loading: false,
      promoCode: '',
      appliedPromo: null,
      defaultProductImage: require('@/assets/default-product.png'),
    };
  },
  computed: {
    // Accès au panier depuis le store Vuex
    cartItems() {
      return this.$store.state.cart || [];
    },
    
    // Vérification de l'authentification
    isAuthenticated() {
      return this.$store.state.token !== null;
    },
    
    // Nombre total d'articles
    totalItems() {
      return this.cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },
    
    // Sous-total calculé
    subtotal() {
      return this.cartItems.reduce((sum, item) => {
        const itemPrice = this.getItemPrice(item.product);
        return sum + (itemPrice * (item.quantity || 1));
      }, 0);
    },
    
    // Économies totales (remises)
    savings() {
      return this.cartItems.reduce((sum, item) => {
        const price = parseFloat(item.product.price) || 0;
        const promoPrice = parseFloat(item.product.promo_price) || price;
        if (promoPrice < price) {
          const discountPerItem = price - promoPrice;
          return sum + (discountPerItem * (item.quantity || 1));
        }
        return sum;
      }, 0);
    },
    
    // Réduction promo
    promoDiscount() {
      return this.appliedPromo ? (this.subtotal * this.appliedPromo.discount / 100) : 0;
    },
    
    // Frais de livraison
    shipping() {
      return this.subtotal >= 50000 ? 0 : 2500;
    },
    
    // Total final
    total() {
      const calculatedTotal = this.subtotal - this.promoDiscount + this.shipping;
      return Math.max(0, calculatedTotal);
    },
    
    // Vérification si on peut procéder au checkout
    canProceedToCheckout() {
      if (!this.isAuthenticated) {
        return false;
      }
      
      if (this.cartItems.length === 0) {
        return false;
      }
      
      // Vérifier que tous les produits sont en stock
      const allInStock = this.cartItems.every(item => 
        item.product.in_stock && item.product.stock_quantity >= (item.quantity || 1)
      );
      
      if (!allInStock) {
        return false;
      }
      
      return true;
    }
  },
  methods: {
    // Gestion des images
    getProductImage(product) {
      if (product.cover_image) {
        return `${this.$store.state.baseUrl}${product.cover_image}`;
      }
      return this.defaultProductImage;
    },
    
    handleImageError(event) {
      event.target.src = this.defaultProductImage;
    },
    
    // Formatage des prix
    formatPrice(price) {
      const priceNumber = parseFloat(price) || 0;
      return new Intl.NumberFormat('fr-FR').format(priceNumber) + ' FCFA';
    },
    
    // Prix d'un produit (promo ou normal)
    getItemPrice(product) {
      const price = parseFloat(product.price) || 0;
      const promoPrice = parseFloat(product.promo_price) || price;
      return promoPrice > 0 ? promoPrice : price;
    },
    
    // Total pour un item
    getItemTotal(item) {
      return this.getItemPrice(item.product) * (item.quantity || 1);
    },
    
    // Calcul du pourcentage de remise
    calculateDiscount(product) {
      const price = parseFloat(product.price) || 0;
      const promoPrice = parseFloat(product.promo_price) || price;
      if (promoPrice > 0 && promoPrice < price) {
        const discount = ((price - promoPrice) / price) * 100;
        return Math.round(discount);
      }
      return 0;
    },
    
    // Gestion des tailles (convertir string en array)
    hasSizes(product) {
      return product.sizes && product.sizes.trim().length > 0;
    },
    
    getSizesArray(sizesString) {
      if (!sizesString) return [];
      // Si c'est déjà un tableau
      if (Array.isArray(sizesString)) return sizesString;
      // Si c'est une string séparée par des virgules
      if (typeof sizesString === 'string') {
        return sizesString.split(',').map(s => s.trim()).filter(s => s.length > 0);
      }
      return [];
    },
    
    // Gestion des couleurs
    hasColors(product) {
      return product.colors && product.colors.trim().length > 0;
    },
    
    getColorsArray(colorsString) {
      if (!colorsString) return [];
      // Si c'est déjà un tableau
      if (Array.isArray(colorsString)) return colorsString;
      // Si c'est une string séparée par des virgules
      if (typeof colorsString === 'string') {
        return colorsString.split(',').map(c => c.trim()).filter(c => c.length > 0);
      }
      return [];
    },
    
    // Couleur de contraste pour les options
    getContrastColor(hexColor) {
      if (!hexColor) return '#000000';
      
      // Supprimer le # si présent
      const color = hexColor.replace('#', '');
      
      // Convertir en RGB
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      
      // Calculer la luminance relative
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Retourner noir ou blanc selon la luminance
      return luminance > 0.5 ? '#000000' : '#ffffff';
    },
    
    // Actions sur les items du panier
    async incrementItem(itemId) {
      try {
        await this.$store.dispatch('incrementCartItem', itemId);
      } catch (error) {
        console.error('Erreur lors de l\'incrémentation:', error);
        alert('Erreur lors de la mise à jour de la quantité');
      }
    },
    
    async decrementItem(itemId) {
      try {
        await this.$store.dispatch('decrementCartItem', itemId);
      } catch (error) {
        console.error('Erreur lors de la décrémentation:', error);
        alert('Erreur lors de la mise à jour de la quantité');
      }
    },
    
    async removeFromCart(itemId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet article du panier ?')) {
        try {
          await this.$store.dispatch('removeFromCart', itemId);
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          alert('Erreur lors de la suppression de l\'article');
        }
      }
    },
    
    async updateSize(itemId, size) {
      try {
        await this.$store.dispatch('updateCartSize', {
          id: itemId,
          size: size
        });
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la taille:', error);
        alert('Erreur lors de la mise à jour de la taille');
      }
    },
    
    async updateColor(itemId, color) {
      try {
        await this.$store.dispatch('updateCartColor', {
          id: itemId,
          color: color
        });
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la couleur:', error);
        alert('Erreur lors de la mise à jour de la couleur');
      }
    },
    
    // Gestion des codes promo
    applyPromoCode() {
      const promoCodes = {
        'BIENVENUE10': 10,
        'PROMO20': 20,
        'VIP15': 15,
        'SOLDES25': 25
      };

      const discount = promoCodes[this.promoCode.toUpperCase()];
      if (discount) {
        this.appliedPromo = {
          code: this.promoCode.toUpperCase(),
          discount: discount
        };
        this.promoCode = '';
        this.$toast?.success?.(`Code promo appliqué: -${discount}%`) || alert(`Code promo appliqué: -${discount}%`);
      } else {
        this.$toast?.error?.('Code promo invalide') || alert('Code promo invalide');
      }
    },
    
    removePromoCode() {
      this.appliedPromo = null;
      this.$toast?.info?.('Code promo supprimé') || alert('Code promo supprimé');
    },
    
    // Navigation vers le checkout
    goToCheckout() {
      if (!this.isAuthenticated) {
        this.$router.push('/login');
        return;
      }
      
      if (!this.canProceedToCheckout) {
        alert('Veuillez vérifier que tous les produits sont disponibles et que vous êtes connecté.');
        return;
      }
      
      this.$router.push('/checkout');
    },
    
    // Charger le panier
    async loadCart() {
      this.loading = true;
      try {
        if (this.isAuthenticated) {
          await this.$store.dispatch('fetchUserData');
        }
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
      } finally {
        this.loading = false;
      }
    }
  },
  async mounted() {
    await this.loadCart();
  }
};
</script>

<style scoped>
.transition-normal {
  transition: all 0.2s ease-in-out;
}

/* Animation pour le spinner */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Amélioration de la lisibilité */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>